import { noop } from '@benzinga/utils';
import {
  GenImgAttrsData,
  GenImgAttrsResult,
  ImageLoaderProps,
  LayoutValue,
  OnLoadingComplete,
  PlaceholderValue,
  StaticImageData,
  StaticImport,
  StaticRequire,
} from './types';

export const extractImageSize = (image: any, size?: string) => {
  if (size && image?.sizes && image?.sizes[size]) {
    return image?.sizes[size];
  }
  return image?.sizes?.original ?? image?.sizes?.medium ?? image?.sizes?.medium_large ?? image?.sizes?.large;
};

export const loadedImageURLs = new Set<string>();

// TODO: Make it dynamic configurable
export const IMAGE_CONFIG_DEFAULT = {
  deviceSizes: [640, 750, 828, 1080, 1200, 1920, 2048, 3840],
  imageSizes: [16, 32, 48, 64, 96, 128, 256, 384],
};

export const emptyDataUrl = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const configDeviceSizes = IMAGE_CONFIG_DEFAULT.deviceSizes;
const configImageSizes = IMAGE_CONFIG_DEFAULT.imageSizes;
const allSizes = [...configDeviceSizes, ...configImageSizes];

configDeviceSizes.sort((a: number, b: number) => a - b);
allSizes.sort((a: number, b: number) => a - b);

export function isStaticRequire(src: StaticRequire | StaticImageData): src is StaticRequire {
  return (src as StaticRequire)?.default !== undefined;
}

export function isStaticImageData(src: StaticRequire | StaticImageData): src is StaticImageData {
  return (src as StaticImageData)?.src !== undefined;
}

export function isStaticImport(src: string | StaticImport): src is StaticImport {
  return typeof src === 'object' && (isStaticRequire(src as StaticImport) || isStaticImageData(src as StaticImport));
}

export function getWidths(
  width: number | undefined,
  layout: LayoutValue,
  sizes: string | undefined,
): { widths: number[]; kind: 'w' | 'x' } {
  if (sizes && (layout === 'fill' || layout === 'responsive')) {
    // Find all the "vw" percent sizes used in the sizes prop
    const viewportWidthRe = /(^|\s)(1?\d?\d)vw/g;
    const percentSizes: number[] = [];

    for (let match; (match = viewportWidthRe.exec(sizes)); match) {
      percentSizes.push(parseInt(match[2]));
    }

    if (percentSizes.length) {
      const smallestRatio = Math.min(...percentSizes) * 0.01;
      return {
        kind: 'w',
        widths: allSizes.filter(s => s >= configDeviceSizes[0] * smallestRatio),
      };
    }

    return { kind: 'w', widths: allSizes };
  }

  if (typeof width !== 'number' || layout === 'fill' || layout === 'responsive') {
    return { kind: 'w', widths: configDeviceSizes };
  }

  const uniqueWidths = new Set(
    // > This means that most OLED screens that say they are 3x resolution,
    // > are actually 3x in the green color, but only 1.5x in the red and
    // > blue colors. Showing a 3x resolution image in the app vs a 2x
    // > resolution image will be visually the same, though the 3x image
    // > takes significantly more data. Even true 3x resolution screens are
    // > wasteful as the human eye cannot see that level of detail without
    // > something like a magnifying glass.
    // https://blog.twitter.com/engineering/en_us/topics/infrastructure/2019/capping-image-fidelity-on-ultra-high-resolution-devices.html
    [width, width * 2].map(w => allSizes.find(p => p >= w) || allSizes[allSizes.length - 1]),
  );

  const widths = Array.from(uniqueWidths.values());

  return { kind: 'x', widths };
}

function getWidth(width: number | Set<number>) {
  return width instanceof Set ? Array.from(width as Set<number>)[0] : width;
}

export function generateImgAttrs({
  crop,
  fastly,
  fetchpriority,
  layout,
  loader,
  loaderProps = {},
  quality,
  sizes,
  src,
  trim,
  width,
}: GenImgAttrsData): GenImgAttrsResult {
  const { kind, widths } = getWidths(width, layout, sizes);
  const last = widths.length - 1;

  return {
    fetchPriority: fetchpriority,
    sizes: !sizes && kind === 'w' ? '100vw' : sizes,

    src: loader({ crop, fastly, quality, src, trim, width: getWidth(widths[last]), ...(loaderProps ?? {}) }),
    // It's intended to keep `src` the last attribute because React updates
    // attributes in order. If we keep `src` the first one, Safari will
    // immediately start to fetch `src`, before `sizes` and `srcSet` are even
    // updated by React. That causes multiple unnecessary requests if `srcSet`
    // and `sizes` are defined.
    // This bug cannot be reproduced in Chrome or Firefox.
    // eslint-disable-next-line sort-keys
    srcSet: widths
      .map(
        (w, i) =>
          `${loader({
            crop,
            fastly,
            quality,
            src,
            trim,
            width: getWidth(w),
            ...(loaderProps ?? {}),
          })} ${kind === 'w' ? getWidth(w) : i + 1}${kind}`,
      )
      .join(', '),
  };
}

export function getInt(x: unknown): number | undefined {
  if (typeof x === 'number') return x;

  if (typeof x === 'string') return parseInt(x, 10);

  return undefined;
}

export function toBase64(str: string) {
  if (typeof window === 'undefined') return Buffer.from(str).toString('base64');

  return window.btoa(str);
}

// See https://stackoverflow.com/q/39777833/266535 for why we use this ref
// handler instead of the img's onLoad attribute.
export function handleLoading(
  img: HTMLImageElement | null,
  src: string,
  layout: LayoutValue,
  placeholder: PlaceholderValue,
  onLoadingComplete?: OnLoadingComplete,
) {
  if (!img) return;

  const handleLoad = () => {
    if (img.src !== emptyDataUrl) {
      const p = 'decode' in img ? img.decode() : Promise.resolve();

      p.catch(noop).then(() => {
        if (placeholder === 'blur') {
          img.style.filter = 'none';
          img.style.backgroundSize = 'none';
          img.style.backgroundImage = 'none';
        }

        loadedImageURLs.add(src);

        if (onLoadingComplete) {
          const { naturalHeight, naturalWidth } = img;
          // Pass back read-only primitive values but not the
          // underlying DOM element because it could be misused.
          onLoadingComplete({ naturalHeight, naturalWidth });
        }

        if (process.env.NODE_ENV !== 'production') {
          if (img.parentElement?.parentElement) {
            const parent = getComputedStyle(img.parentElement.parentElement);
            if (!parent.position) {
              // The parent has not been rendered to the dom yet and therefore it has no position. Skip the warnings for such cases.
            } else if (layout === 'responsive' && parent.display === 'flex') {
              console.warn(
                `Image with src "${src}" may not render properly as a child of a flex container. Consider wrapping the image with a div to configure the width.`,
              );
            } else if (layout === 'fill' && parent.position !== 'relative' && parent.position !== 'fixed') {
              console.warn(
                `Image with src "${src}" may not render properly with a parent using position:"${parent.position}". Consider changing the parent style to position:"relative" with a width and height.`,
              );
            }
          }
        }
      });
    }
  };

  if (img.complete) {
    // If the real image fails to load, this will still remove the placeholder.
    // This is the desired behavior for now, and will be revisited when error
    // handling is worked on for the image component itself.
    handleLoad();
  } else {
    img.onload = handleLoad;
  }
}

/**
 * This is de default loader supposed to be used with Fastly's CDN.
 */
export function defaultImageLoader(loaderProps: ImageLoaderProps): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const load = (..._args: any[]) => {
    const devicePixelRatio = typeof window === 'undefined' ? 2 : window.devicePixelRatio || 2;
    let query = loaderProps.src + `?optimize=medium&dpr=${devicePixelRatio}&auto=webp&width=${loaderProps.width}`;

    if (loaderProps.crop) query += `&crop=${loaderProps.crop}`;
    if (loaderProps.trim) query += `&trim=${loaderProps.trim}`;
    if (loaderProps.fastly) query += `&${loaderProps.fastly}`;

    return query;
  };

  if (load) {
    return load({ ...loaderProps });
  }

  throw new Error(`Unknown "loader" found`);
}

export function getPreconnectUrl(url: string): string | null {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('/')) return null;
  if (!url.startsWith('http://') && !url.startsWith('https://')) return null;

  try {
    const urlObj = new URL(url);
    return urlObj.origin;
  } catch (error) {
    console.error('Invalid URL provided:', error);
    return null;
  }
}
